import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { APPLICATION_NAME_TOKEN, ApplicationName, PdfCustomisationMode, ViewCategory } from '@ddt-web/config';
import { InjectConfig, LocaleUtilResolverService, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { ApplicationNames, SharedApplicationConstants, WebUserDataConstants } from '@ddt-web/domain';
import { StoreService } from '@ddt-web/store-service';
import * as get from 'lodash/get';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { PdfGenerateStoreConfig } from '../config';
import { Countries } from '../enums/countries.enum';
import { PdfReportConfig } from '../models';

@Injectable({
	providedIn: 'root'
})
export class PdfReportConfigService {
	@InjectConfig(ModelsExperienceConfigKey.SHOW_CMS_PDF_MODULARITY) private readonly showCmsPdfModularity: boolean;

	constructor(
		private storeService: StoreService,
		private http: HttpClient,
		private servicePathProvider: ServicePathProviderService,
		private localeUtilResolverService: LocaleUtilResolverService,
		@Inject(APPLICATION_NAME_TOKEN) private applicationName: ApplicationName
	) { }

	/** add new json and yaml file in teamsite incase new configs are required for new application.
	 *	@params - appName  - to be replaced by the applicationName

	 *  URL Reference TeamSite for json file (contains config):
	 * //tstccms001/default/main/BlackRock/WebTools/WORKAREA/shared/config-wds/data_store/bfa/appName/advisor-pdf-report
	 *
	 *  URL Reference Teamsite for yaml file:
	 * //tstccms001/default/main/BlackRock/WebTools/WORKAREA/shared/config-wds/requests/defaults/bfa/appName/config
	 */
	public fetchPdfReportConfig(reportType: string, mode?: string): Observable<PdfReportConfig> {
		let toolsPath = WebUserDataConstants.CONFIGS;
		if (this.applicationName === ApplicationNames.PORTFOLIO_ANALYSER) {
			toolsPath += WebUserDataConstants.APPLICATIONS.BFA;
		} else if (this.applicationName === ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION) {
			toolsPath += ApplicationNames.PORTFOLIO_CENTRE + ':' + WebUserDataConstants.APPLICATIONS.BPAS;
		} else if (SharedApplicationConstants.PC_APPS.includes(this.applicationName)) {
			toolsPath += ApplicationNames.PORTFOLIO_CENTRE;
		} else if (this.applicationName === ApplicationNames.MODELS_BROWSE) {
			toolsPath += WebUserDataConstants.APPLICATIONS.BFA + ':' + WebUserDataConstants.APPLICATIONS.MVAL;
		} else if (this.applicationName === ApplicationNames.INVESTMENT_QUALITY) {
			toolsPath += WebUserDataConstants.APPLICATIONS.BFA + ':iq';
		} else if (this.applicationName === ApplicationNames.MODELS_EXPERIENCE) {
			toolsPath += WebUserDataConstants.APPLICATIONS.BFA;
			const locale = this.localeUtilResolverService.getResolvedLocaleValue();
			let suffix: string;
			if (locale === Countries.AU) {
				suffix = ':me-au';
			} else if (locale === Countries.LM) {
				suffix = ':me-lm';
			} else {
				suffix = ':me';
			}
			toolsPath += suffix;
		}
		switch (reportType) {
			case ViewCategory.FA_PLUS_PDF:
				toolsPath += WebUserDataConstants.PS_REPORT_CONFIG;
				break;
			case ViewCategory.ADVISOR_PDF:
				toolsPath += WebUserDataConstants.ADVISOR_REPORT_CONFIG;
				break;
			case ViewCategory.CLIENT_PDF:
				toolsPath += WebUserDataConstants.CLIENT_REPORT_CONFIG;
				break;
			case ViewCategory.PROPOSAL_PDF:
				toolsPath += mode === PdfCustomisationMode.SNAPSHOT ? WebUserDataConstants.PROPOSAL_SNAPSHOT_REPORT_CONFIG : WebUserDataConstants.PROPOSAL_REPORT_CONFIG;
				break;
			case ViewCategory.FA_SELF_SERVICE_PDF:
				toolsPath += WebUserDataConstants.ADVISOR_REPORT_CONFIG;
		}

		return this.http.get(this.servicePathProvider.getServicePath('api-gateway') + toolsPath).pipe(
			map(jsonResponse => {
				const config = { sections: get(jsonResponse, 'resultMap.REPORT[0].data.sections', []) };
				if (this.showCmsPdfModularity) {
					config['cmsSections'] = { sections: get(jsonResponse, 'resultMap.REPORT[0].data.cmsSections.sections', []) };
				}
				return config as PdfReportConfig;
			}),
			catchError(error => {
				console.error('PDF rules config loading error:', error);
				return of({ sections: [] } as PdfReportConfig);
			})
		);
	}

	public getConfigFromStore(storeName: string): Observable<any> {
		return this.storeService.selectAll(PdfGenerateStoreConfig.reportConfig).pipe(
			map(data => (data && Object.prototype.hasOwnProperty.call(data, storeName) ?
				data[storeName] : null))
		);
	}

	public addConfigToStore(storeName: string, config: PdfReportConfig): void {
		this.storeService.upsertOne(PdfGenerateStoreConfig.reportConfig, {
			[storeName]: config
		});
	}

}
